import type { WorkOrderType } from "../enums";

/**
 * Expenses (ADR 0021): money paid for a vehicle, or for the
 * garage, that isn't a part from stock. Each has a cost type from a fixed list and a purpose,
 * running costs or events & motorsport, which the cost type suggests.
 */

export const COST_TYPES = [
  "fuel",
  "charging",
  "workshop",
  "roadworthiness_fee",
  "insurance",
  "vehicle_tax",
  "registration_fees",
  "care",
  "parking_tolls",
  "accessories",
  "event_fee",
  "other",
] as const;
export type CostType = (typeof COST_TYPES)[number];

/** Running costs, events & motorsport, or other (neither fits); no cost type suggests "other". */
export const EXPENSE_PURPOSES = ["running", "event", "other"] as const;
export type ExpensePurpose = (typeof EXPENSE_PURPOSES)[number];

interface CostTypeRule {
  /** The purpose an expense of this type starts with. */
  purpose: ExpensePurpose;
  /** Whether VAT is paid on it; insurance premiums and fees to the state carry none. */
  vat: boolean;
}

export const COST_TYPE_RULES: Readonly<Record<CostType, CostTypeRule>> = {
  fuel: { purpose: "running", vat: true },
  charging: { purpose: "running", vat: true },
  workshop: { purpose: "running", vat: true },
  roadworthiness_fee: { purpose: "running", vat: true },
  insurance: { purpose: "running", vat: false },
  vehicle_tax: { purpose: "running", vat: false },
  registration_fees: { purpose: "running", vat: false },
  care: { purpose: "running", vat: true },
  parking_tolls: { purpose: "running", vat: true },
  accessories: { purpose: "running", vat: true },
  event_fee: { purpose: "event", vat: true },
  other: { purpose: "running", vat: true },
};

export function defaultPurpose(costType: CostType): ExpensePurpose {
  return COST_TYPE_RULES[costType].purpose;
}

/** The rate an expense starts with: none for VAT-free types, else the garage's on its date. */
export function defaultTaxRate(costType: CostType, rateOnDate: number): number {
  return COST_TYPE_RULES[costType].vat ? rateOnDate : 0;
}

/** The cost type an invoice added to a work order starts with: the inspection's fee, else workshop. */
export function invoiceCostType(workOrderType: WorkOrderType): CostType {
  return workOrderType === "roadworthiness_inspection" ? "roadworthiness_fee" : "workshop";
}

/**
 * The date an invoice added to a work order starts on: the day it was completed, else its planned
 * day once that has come; null leaves it to today. `today` is the UTC date.
 */
export function invoiceDate(
  workOrder: { completedOn: string | null; plannedOn: string | null },
  today: string,
): string | null {
  if (workOrder.completedOn) return workOrder.completedOn;
  return workOrder.plannedOn && workOrder.plannedOn <= today ? workOrder.plannedOn : null;
}

/** Amounts shown on the garage's price basis, added up: in total and per purpose. */
export interface ExpenseTotals {
  totalMinor: number;
  runningMinor: number;
  eventMinor: number;
  otherMinor: number;
  currency: string;
}

export function sumExpenses(
  rows: readonly { amountMinor: number; purpose: ExpensePurpose }[],
  currency: string,
): ExpenseTotals {
  const totals: ExpenseTotals = {
    totalMinor: 0,
    runningMinor: 0,
    eventMinor: 0,
    otherMinor: 0,
    currency,
  };
  for (const row of rows) {
    totals.totalMinor += row.amountMinor;
    totals[`${row.purpose}Minor`] += row.amountMinor;
  }
  return totals;
}

export interface ExpenseMonth<T> {
  /** "YYYY-MM". */
  month: string;
  totals: ExpenseTotals;
  rows: T[];
}

/** Rows grouped by the month they were paid in, newest month first, rows newest first. */
export function groupByMonth<
  T extends { spentOn: string; amountMinor: number; purpose: ExpensePurpose },
>(rows: readonly T[], currency: string): ExpenseMonth<T>[] {
  const sorted = [...rows].sort((a, b) =>
    a.spentOn < b.spentOn ? 1 : a.spentOn > b.spentOn ? -1 : 0,
  );
  const months = new Map<string, T[]>();
  for (const row of sorted) {
    const month = row.spentOn.slice(0, 7);
    const list = months.get(month);
    if (list) list.push(row);
    else months.set(month, [row]);
  }
  return [...months].map(([month, list]) => ({
    month,
    totals: sumExpenses(list, currency),
    rows: list,
  }));
}

/** Totals per cost type, largest first; types without expenses are left out. */
export function totalsByCostType(
  rows: readonly { costType: CostType; amountMinor: number }[],
): { costType: CostType; totalMinor: number }[] {
  const totals = new Map<CostType, number>();
  for (const row of rows)
    totals.set(row.costType, (totals.get(row.costType) ?? 0) + row.amountMinor);
  return [...totals]
    .map(([costType, totalMinor]) => ({ costType, totalMinor }))
    .sort(
      (a, b) =>
        b.totalMinor - a.totalMinor ||
        COST_TYPES.indexOf(a.costType) - COST_TYPES.indexOf(b.costType),
    );
}
